/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, forwardRef, useRef } from 'react';
import * as Highcharts from 'highcharts';
import _ from 'lodash';
import classnames from 'classnames';
import useTheme from '@hooks/useTheme';
import { xAxisStyleDefault } from './config';
import { defaultToolTip } from './config';
import clear_filter_icon from '@assets/imgs/icons/clear_filter.png';
import { NoDataWithName } from '@components';
import D_Currency from '@utils/currency.js';

const tooltipPrefix = function (that) {
  return (
    `<b>${that.point.category}</b>` +
    `<br>` +
    `<b>${that.series.name}</b>` +
    `: `
  );
};

const defaultBarChartOption = (currentCashType) => ({
  chart: {
    type: 'column',
    style: {
      'font-family': 'Helvetica,Arial',
    },
  },
  title: {
    align: 'left',
    style: {
      'font-family': 'HelveticaNeue-Medium,Arial,sans-serif',
      'font-size': '1rem',
      color: '#DBDBDB !important',
    },
    margin: 30,
  },
  colors: ['#0081E9'],
  tooltip: {
    ...defaultToolTip,
    formatter() {
      return (
        tooltipPrefix(this) +
        D_Currency.format({
          value: this.y,
          currencyIsoCode: this.series.options.unitType,
        })
      );
    },
  },
  xAxis: {
    lineColor: '#666',
    labels: {
      style: xAxisStyleDefault,
      autoRotation: false,
      // formatter() {
      // return this.value.replace(', 20', '');
      // },
    },
    title: {
      text: null,
    },
  },
  yAxis: [
    {
      title: {
        text: undefined,
      },
      gridLineColor: '#666',
      gridLineDashStyle: 'longdash',
      labels: {
        formatter() {
          return D_Currency.format(
            {
              value: this.value,
              currencyIsoCode: currentCashType,
            },
            0,
          );
        },
        style: {
          'font-family': 'HelveticaNeue,Arial !important',
          'font-size': '0.875rem',
          // color: '#ACACAC !important',
        },
      },
      softMax: 0,
      softMin: 0,
    },
  ],
  series: [],
  legend: {
    symbolHeight: 10,
    symbolWidth: 10,
    symbolRadius: 0,
    itemStyle: {
      'font-family': 'HelveticaNeue,Arial !important',
      'font-weight': 'normal',
      'font-size': '0.75rem',
      color: '#333333 !important',
    },
    // useHTML: true,
    // title: {
    //   text: 'fdasfas',
    // },
  },
  credits: {
    enabled: false,
  },
  plotOptions: {
    column: {
      maxPointWidth: 60,
    },
    series: {
      events: {
        // legendItemClick: () => false,
        legendItemClick() {
          if (this.name === 'Show All') {
            this.chart.series.forEach((series) => {
              if (series.name !== this.name) {
                series.setVisible(true, false);
              }
            });
            this.chart.redraw();
            return false;
          }
          const showModeOn = this.chart.series.some(
            (series) => series.visible === false,
          );
          if (showModeOn) {
            return true;
          }
          const clickedSeries = this;
          this.chart.series.forEach((series) => {
            if (series.name !== this.name && series.name !== 'Show All') {
              series.setVisible(!series.visible, false);
            }
          });
          this.chart.redraw();
          return false;
        },
      },
    },
  },
});

const combineBarChartOption = (dynamicBarChartOption, basicBarChartOption) => {
  let finalOption = basicBarChartOption;
  const dynamicOptions = Object.keys(dynamicBarChartOption);
  for (let i = 0; i < dynamicOptions.length; i++) {
    for (dynamicOptions[i] in basicBarChartOption) {
      if (basicBarChartOption[dynamicOptions[i]] instanceof Array) {
        finalOption = {
          ...finalOption,
          [dynamicOptions[i]]:
            dynamicBarChartOption[dynamicOptions[i]] ||
            basicBarChartOption[dynamicOptions[i]],
        };
      } else {
        finalOption = {
          ...finalOption,
          [dynamicOptions[i]]: {
            ...finalOption[dynamicOptions[i]],
            ...dynamicBarChartOption[dynamicOptions[i]],
          },
        };
      }
    }
  }
  return finalOption;
};

const buildDynamicBarChartOption = (propsOptions, currentCashType) => {
  let finalDynamicOption = {};
  if ('containerId' in propsOptions) {
    finalDynamicOption = {
      ...finalDynamicOption,
      chart: { renderTo: propsOptions.containerId },
    };
  }
  if ('companyName' in propsOptions) {
    finalDynamicOption = {
      ...finalDynamicOption,
      title: {
        text: propsOptions.companyName,
      },
    };
  }
  if ('categories' in propsOptions) {
    finalDynamicOption = {
      ...finalDynamicOption,
      xAxis: {
        categories: propsOptions.categories,
      },
    };
  }
  if ('series' in propsOptions) {
    const defaultSeriesOption = {
      borderColor: 'transparent',
      borderRadius: 2,
      tooltip: {
        headerFormat: null,

        pointFormatter() {
          const str = `<b style="font-size: 0.75rem; font-weight: bold">${this.category.replace(
            ', 20',
            '',
          )}</b>
          <br/>
          <b>${this.series.name}</b>: ${D_Currency.format({
            value: this.y,
            currencyIsoCode: currentCashType,
          })}<br/>`;

          return str;
        },
      },
    };
    finalDynamicOption = {
      ...finalDynamicOption,
      series: propsOptions.series.map((seryItem) => ({
        ...seryItem,
        ...defaultSeriesOption,
      })),
    };
  }
  if ('secondaryXAxis' in propsOptions) {
    finalDynamicOption.xAxis.title = {
      text: propsOptions.secondaryXAxis,
      margin: 15,
    };
  }
  return finalDynamicOption;
};

const buildBarChartOption = (propsOptions, currentCashType) => {
  try {
    const dynamicBarChartOption = buildDynamicBarChartOption(
      propsOptions,
      currentCashType,
    );
    const finalOption = combineBarChartOption(
      dynamicBarChartOption,
      defaultBarChartOption(currentCashType),
    );

    // Defensive programming: ensure yAxis exists and is an array
    if (
      !finalOption.yAxis ||
      !Array.isArray(finalOption.yAxis) ||
      finalOption.yAxis.length === 0
    ) {
      console.warn('yAxis not properly initialized, using default');
      finalOption.yAxis = [
        {
          title: { text: undefined },
          gridLineColor: '#666',
          gridLineDashStyle: 'longdash',
          labels: {
            formatter() {
              return D_Currency.format(
                {
                  value: this.value,
                  currencyIsoCode: currentCashType,
                },
                0,
              );
            },
            style: {
              'font-family': 'HelveticaNeue,Arial !important',
              'font-size': '0.875rem',
            },
          },
          softMax: 0,
          softMin: 0,
        },
      ];
    }

    // Now safely access yAxis[0]
    if ('yMax' in propsOptions && propsOptions.yMax != null) {
      const suffix = D_Currency.format({
        value: propsOptions.yMax,
      }).slice(-1);
      finalOption.yAxis[0].softMax = propsOptions.yMax;
      finalOption.yAxis[0].labels.formatter = function () {
        return D_Currency.formatWithCertainSuffix(
          this.value,
          suffix,
          1,
          currentCashType,
        );
      };
    }

    if ('yMin' in propsOptions && propsOptions.yMin != null) {
      finalOption.yAxis[0].softMin = propsOptions.yMin;
    }

    return finalOption;
  } catch (error) {
    console.error('Error in buildBarChartOption:', error);
    // Return a safe default option
    return defaultBarChartOption(currentCashType);
  }
};

const BarChart = forwardRef(
  (
    {
      containerId,
      companyName,
      categories,
      series,
      secondaryXAxis,
      noDataFlag,
      yMax,
      yMin,
    },
    ref,
  ) => {
    const { theme } = useTheme();
    const isLightMode = theme === 'light';
    const noData =
      _.compact(_.flatten(series.map((item) => item.data || []))).length ===
        0 || noDataFlag;

    const clearFilters = (chart) => {
      try {
        chart.series.forEach((series) => {
          series.setVisible(true);
        });
        filterImageRef.current && filterImageRef.current.hide();
      } catch (_unused) {
        console.log('not have chart');
      }
    };
    const filterImageRef = useRef({});

    const showHideFilterIcon = (chart, event) => {
      if (!chart) {
        return;
      }
      const legendItems = _.cloneDeep(chart.legend.allItems);
      if (legendItems && legendItems.length) {
        let hasHide = false;
        for (let i = 0; i < legendItems.length; i++) {
          if (event && legendItems[i].name === event.target.name && !hasHide) {
            hasHide = legendItems[i].visible;
          } else if (!legendItems[i].visible && !hasHide) {
            hasHide = true;
          }
        }
        if (hasHide) {
          filterImageRef.current && filterImageRef.current.show();
        } else {
          filterImageRef.current && filterImageRef.current.hide();
        }
      }
    };

    const highchartsCallback = (chart) => {
      filterImageRef.current = chart.renderer
        .image(clear_filter_icon, 15, chart.chartHeight - 39, 14, 14)
        .attr({
          className: `clear-filter-icon-${containerId}`,
        })
        .on('click', () => {
          clearFilters(chart);
        })
        .on('mouseover', function (e) {
          const tooltipDom = document.querySelector(
            `.chart-content-${containerId}`,
          ).firstElementChild;
          if (!tooltipDom.classList.contains('tooltip-visibility')) {
            const chartOffsetLeft =
              (chart.container && chart.container.offsetLeft) || 0;
            tooltipDom.classList.add('tooltip-visibility');
            tooltipDom.style.top = `${e.layerY - 50}px`;
            tooltipDom.style.left = `${e.layerX - 38 + chartOffsetLeft}px`;
          }
        })
        .on('mouseout', () => {
          document
            .querySelector(`.chart-content-${containerId}`)
            .firstElementChild.classList.remove('tooltip-visibility');
        })
        .css({
          width: '0.875rem',
          height: '0.875rem',
          cursor: 'pointer',
        })
        .add();
      filterImageRef.current && filterImageRef.current.hide();
    };

    useEffect(() => {
      if (!series || series.length === 0 || !containerId) {
        return;
      }

      const barChartOption = buildBarChartOption(
        {
          containerId,
          companyName: companyName || '-',
          categories,
          series,
          secondaryXAxis,
          yMax,
          yMin,
        },
        series.length ? series[0].unitType : 'USD',
      );

      try {
        // Theme styling
        if (isLightMode) {
          barChartOption.tooltip.style.color = '#333333';
        } else {
          barChartOption.tooltip.style.color = '#dbdbdb';
        }

        // Legend configuration
        barChartOption.legend.title = {
          text: barChartOption.xAxis.title.text
            ? `<span style="font-size: 0.875rem; color: #666; font-weight: normal">${barChartOption.xAxis.title.text}</span>`
            : '',
          style: {
            fontStyle: 'normal',
          },
        };
        barChartOption.xAxis.title.text = '';

        // Chart events
        barChartOption.chart.events = {
          render() {
            const legend = this.legend;
            const title = legend.title;
            if (title) {
              const legendWidth = legend.legendWidth;
              const titleWidth = title.width;
              const diff = legendWidth - titleWidth;
              let offsetX = diff / 2;
              if (titleWidth >= legendWidth - 8) {
                offsetX -= diff;
              }
              title.attr({ x: offsetX });
            }
          },
        };

        // Plot options
        barChartOption.plotOptions = {
          column: {
            stacking: 'normal',
            dataLabels: {
              enabled: false,
            },
          },
          series: {
            events: {
              legendItemClick(event) {
                showHideFilterIcon(this.chart, event);
              },
            },
          },
        };

        if (typeof Highcharts === 'object' && Highcharts.chart) {
          // Destroy existing chart if it exists
          if (ref.current) {
            ref.current.destroy();
          }

          const barChart = Highcharts.chart(barChartOption, (chart) => {
            highchartsCallback(chart);
          });

          ref.current = barChart;
        } else {
          console.error('Highcharts is not available or chart method missing');
        }
      } catch (err) {
        console.error('Error creating chart:', err);
        console.error('Error stack:', err.stack);
      }
    }, [
      Highcharts,
      containerId,
      companyName,
      categories,
      series,
      secondaryXAxis,
      yMax,
      yMin,
      isLightMode,
    ]);

    return (
      <div
        className={classnames(
          'gantt-chart-content',
          'clear-filter-tooltip',
          `chart-content-${containerId}`,
          {
            'highcharts-dark': !isLightMode,
            'highcharts-light': isLightMode,
          },
        )}
      >
        <p className="tooltip-text">Clear Filter</p>
        {noData && (
          <NoDataWithName key={`key-${containerId}`} name={companyName} />
        )}
        <div id={containerId} style={{ display: noData ? 'none' : '' }} />
      </div>
    );
  },
);

export default React.memo(BarChart);
